const CART_ANALYTICS_EVENT = 'cart:v2:analytics';

function cartDevice() {
  return window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
}

export function trackCartEvent(name, detail = {}) {
  const payload = {
    event: `pp_${name}`,
    cart_device: cartDevice(),
    ...detail,
  };

  window.dispatchEvent(new CustomEvent(CART_ANALYTICS_EVENT, { detail: payload }));
  if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
}

class CartRecommendations extends HTMLElement {
  #controller;
  #coverageMap;
  #impressionObserver;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#coverageMap = this.#parseCoverageMap();
    void this.#initialise();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#impressionObserver?.disconnect();
  }

  async #initialise() {
    if (['curated', 'dynamic'].includes(this.dataset.strategy) && this.dataset.productId) {
      await this.#loadComplementaryProducts();
    }
    if (!this.hidden) this.#observeImpression();
  }

  async #loadComplementaryProducts() {
    try {
      const seedProductIds = (this.dataset.seedProductIds || this.dataset.productId || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .slice(0, 3);
      const productLists = await Promise.all(seedProductIds.map((id) => this.#fetchComplementaryProducts(id)));
      const products = this.#roundRobinProducts(productLists);
      const productIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
      const excludedHandles = new Set(
        (this.dataset.excludedHandles || '')
          .split(',')
          .map((handle) => this.#normaliseHandle(handle))
          .filter(Boolean)
      );
      const cartCoverage = new Set(
        (this.dataset.cartCoverage || '').split(',').map((token) => token.trim()).filter(Boolean)
      );
      const selectedCoverage = new Set();
      const selectedProducts = [];
      const selectedProductIds = new Set();
      const selectedHandles = new Set();
      const fallbackCards = Array.from(this.querySelectorAll('.pp-cart-recommendation'));
      const selectedFallbackCards = [];
      const recommendationLimit = Number(this.dataset.limit) || 2;

      for (const product of products) {
        if (selectedProducts.length >= recommendationLimit) break;
        const handle = this.#normaliseHandle(product.handle);
        if (!product.available || productIds.has(String(product.id)) || excludedHandles.has(handle)) continue;

        const footprint = this.#productCoverage(product, { rejectCombo: true });
        if (!footprint || this.#setsOverlap(footprint, cartCoverage) || this.#setsOverlap(footprint, selectedCoverage)) {
          continue;
        }

        selectedProducts.push(product);
        selectedProductIds.add(String(product.id));
        selectedHandles.add(handle);
        footprint.forEach((token) => selectedCoverage.add(token));
      }

      if (!selectedProducts.length) return;

      for (const card of fallbackCards) {
        if (selectedProducts.length + selectedFallbackCards.length >= recommendationLimit) break;
        const productId = card.dataset.productId || '';
        const handle = this.#normaliseHandle(card.dataset.productHandle);
        if (selectedProductIds.has(productId) || selectedHandles.has(handle) || excludedHandles.has(handle)) continue;

        const footprint = new Set(
          (card.dataset.productCoverage || '')
            .split(',')
            .map((token) => token.trim())
            .filter(Boolean)
        );
        if (!footprint.size || this.#setsOverlap(footprint, cartCoverage) || this.#setsOverlap(footprint, selectedCoverage)) {
          continue;
        }

        selectedFallbackCards.push(card);
        footprint.forEach((token) => selectedCoverage.add(token));
      }

      this.#render(selectedProducts, selectedFallbackCards);
      this.dataset.recommendationSource = selectedFallbackCards.length
        ? 'complementary+ritual-map'
        : 'complementary';
    } catch (error) {
      if (error?.name !== 'AbortError') this.dataset.recommendationSource = 'fallback';
    }
  }

  async #fetchComplementaryProducts(productId) {
    const url = new URL(this.dataset.url || '/recommendations/products.json', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', 'complementary');

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.#controller.signal,
      });
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      if (error?.name === 'AbortError') throw error;
      return [];
    }
  }

  #roundRobinProducts(productLists) {
    const products = [];
    const seenProductIds = new Set();
    const maxListLength = Math.max(0, ...productLists.map((list) => list.length));

    for (let index = 0; index < maxListLength; index += 1) {
      for (const list of productLists) {
        const product = list[index];
        if (!product || seenProductIds.has(String(product.id))) continue;
        seenProductIds.add(String(product.id));
        products.push(product);
      }
    }

    return products;
  }

  #parseCoverageMap() {
    const map = new Map();
    (this.dataset.coverageMap || '').split(';').forEach((entry) => {
      const separator = entry.indexOf(':');
      if (separator < 1) return;
      const handle = this.#normaliseHandle(entry.slice(0, separator));
      const coverage = entry
        .slice(separator + 1)
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean);
      if (handle && coverage.length) map.set(handle, new Set(coverage));
    });
    return map;
  }

  #productCoverage(product, { rejectCombo = false } = {}) {
    const handle = this.#normaliseHandle(product.handle);
    const comboText = `${handle} ${product.title || ''}`.toLowerCase();
    const isCombo = /\b(combo|bundle|ritual|pack|kit|duo|set)\b/.test(comboText.replace(/[-_]+/g, ' '));
    if (rejectCombo && isCombo) return null;

    const mappedCoverage = this.#coverageMap?.get(handle);
    if (mappedCoverage) return new Set(mappedCoverage);

    if (isCombo) return null;
    return handle ? new Set([handle]) : null;
  }

  #normaliseHandle(handle) {
    return String(handle || '').trim().toLowerCase();
  }

  #setsOverlap(first, second) {
    for (const token of first) {
      if (second.has(token)) return true;
    }
    return false;
  }

  #render(products, fallbackCards = []) {
    const list = this.querySelector('[data-cart-recommendation-list]');
    if (!list) return;

    const fragment = document.createDocumentFragment();
    products.forEach((product) => fragment.append(this.#productCard(product)));
    fallbackCards.forEach((card) => fragment.append(card));
    list.replaceChildren(fragment);
    this.dataset.preloadedCount = String(products.length + fallbackCards.length);
    this.hidden = false;
  }

  #productCard(product) {
    const article = document.createElement('article');
    article.className = 'pp-cart-recommendation';
    article.dataset.productId = String(product.id);
    article.dataset.productHandle = this.#normaliseHandle(product.handle);

    const mediaLink = document.createElement('a');
    mediaLink.className = 'pp-cart-recommendation__media';
    mediaLink.href = product.url;
    mediaLink.tabIndex = -1;
    mediaLink.setAttribute('aria-hidden', 'true');

    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 88;
      image.height = 88;
      mediaLink.append(image);
    }

    const details = document.createElement('div');
    details.className = 'pp-cart-recommendation__details';

    const title = document.createElement('a');
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('span');
    price.textContent = this.#formatMoney(product.price);
    details.append(title, price);

    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant.available !== false);
    const variantToAdd = availableVariants[0];
    let action;

    if (variantToAdd) {
      action = document.createElement('button');
      action.type = 'button';
      action.textContent = 'Add';
      action.dataset.ppFbtAdd = '';
      action.dataset.ppCartAddon = '';
      action.dataset.variantId = String(variantToAdd.id);
      action.setAttribute('aria-label', `Add ${product.title} to cart`);
    } else {
      action = document.createElement('a');
      action.href = product.url;
      action.textContent = 'Choose';
    }

    action.className = 'pp-cart-recommendation__action';
    article.append(mediaLink, details, action);
    return article;
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: this.dataset.currency || 'INR',
        maximumFractionDigits: 0,
      }).format(Number(cents) / 100);
    } catch (_error) {
      return `₹${Math.round(Number(cents) / 100)}`;
    }
  }

  #observeImpression() {
    if (!('IntersectionObserver' in window)) return;
    this.#impressionObserver?.disconnect();
    this.#impressionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        trackCartEvent('cart_recommendation_impression', {
          recommendation_source: this.dataset.recommendationSource || this.dataset.source || 'fallback',
        });
        this.#impressionObserver?.disconnect();
      },
      { threshold: 0.35 }
    );
    this.#impressionObserver.observe(this);
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}

document.addEventListener('click', (event) => {
  const element = event.target instanceof Element ? event.target : null;
  const recommendationAction = element?.closest('.pp-cart-recommendation__action');
  if (recommendationAction) {
    trackCartEvent('cart_recommendation_click', {
      action: recommendationAction.hasAttribute('data-pp-fbt-add') ? 'add' : 'choose',
      product_id: recommendationAction.closest('[data-product-id]')?.dataset.productId,
    });
  }

});

document.addEventListener('cart:update', (event) => {
  const source = event.detail?.data?.source || event.detail?.sourceId || 'unknown';
  if (source === 'cart-items-component') {
    trackCartEvent(event.detail?.data?.action === 'clear' ? 'cart_remove_success' : 'cart_quantity_success');
  } else if (source === 'cart-undo') {
    trackCartEvent('cart_remove_undo');
  }
});

document.addEventListener('discount:update', () => {
  trackCartEvent('cart_discount_success');
});

document.addEventListener('cart:error', (event) => {
  trackCartEvent('cart_mutation_failure', {
    source: event.detail?.sourceId || 'cart',
  });
});
